import { Column, flexRender, Table } from '@tanstack/react-table';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { themeGet } from '@upright/ui/common/generic/theme/custom-theme';
import { CssFromFunction } from '@upright/ui/common/generic/types/types';
import Skeleton from 'react-loading-skeleton';
import styled, { css } from 'styled-components';
import { TableStyleProps } from '../../types';
import { getFirstAndLastColumnIds } from '../../utils/utils';
import { getTableCellCommonStyle, TableCellCommonStyleProps } from './table-cell-common';

export function TFoot<T extends object>(props: {
  table: Table<T>;
  getColumnProps?: (column: Column<T>) => Partial<TableStyleProps>;
  showVerticalBorders: boolean;
  className?: string;
}) {
  const { firstColumnId, lastColumnId } = getFirstAndLastColumnIds(props.table);
  return (
    <TFootRoot className={props.className}>
      {props.table.getFooterGroups().map(footerGroup => (
        <Tr key={footerGroup.id}>
          {footerGroup.headers.map(header => {
            const column = header.column;
            const columnDef = column.columnDef;
            const footerDef = columnDef.footer;
            const footerElement =
              typeof footerDef === 'string' ? <DefaultFooter>{footerDef}</DefaultFooter> : footerDef;
            const meta = columnDef.meta;

            const columnProps = props.getColumnProps?.(column);
            // footerProps = props.getFooterProps?.(column); // Can add support if needed

            const mergedStyle = columnProps?.style || {};
            const mergedCss: CssFromFunction = [
              ...(meta?.css || []),
              ...(meta?.footerCss || []),
              ...(columnProps?.css || []),
            ];

            return (
              <Td
                key={header.id}
                style={mergedStyle}
                css={mergedCss}
                align={meta?.titleAlign || meta?.align || 'left'}
                isFirstColumn={column.id === firstColumnId}
                isLastColumn={column.id === lastColumnId}
                showVerticalBorders={props.showVerticalBorders}
              >
                {header.isPlaceholder ? (
                  <Skeleton />
                ) : (
                  <FooterCellWrap>{flexRender(footerElement, header.getContext())}</FooterCellWrap>
                )}
              </Td>
            );
          })}
        </Tr>
      ))}
    </TFootRoot>
  );
}

const TFootRoot = styled.tfoot`
  /* Don't add any styling here, it won't matter because of display: contents */
  display: contents;
`;

const Tr = styled.tr<{ css?: CssFromFunction }>`
  /* Don't add any styling here, it won't matter because of display: contents */
  display: contents;
`;

const Td = styled.td<TableCellCommonStyleProps>`
  ${props =>
    getTableCellCommonStyle({
      align: props.align,
      isFirstColumn: props.isFirstColumn,
      isLastColumn: props.isLastColumn,
    })};
  ${props =>
    props.showVerticalBorders
      ? css`
          &:not(:last-of-type) {
            border-right: 1px solid ${BouColor.PLATFORM_BORDER_GRAY_LIGHTER};
          }
        `
      : ''}
  font-weight: 500;
  padding-top: ${themeGet('space.1')};
  padding-bottom: ${themeGet('space.1')};
  border-top: 1px solid ${BouColor.PLATFORM_BORDER_GRAY_LIGHTER};
  min-height: 3.1em;
  ${props => props.css};
`;

const FooterCellWrap = styled.div`
  display: flex;
  align-items: center;
  max-width: 100%;
  height: 100%;
`;

const DefaultFooter = styled.span`
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
